import {
  Box,
  Center,
  Flex,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Radio,
  RadioGroup,
  Text,
  useDisclosure,
  VStack,
  Wrap,
} from "@chakra-ui/react";
import { ReactComponent as CampaignCallout } from "../../../common/img/campaign.svg";
import {
  FaEllipsisV,
  FaExclamationCircle,
  FaPauseCircle,
  FaPlayCircle,
} from "react-icons/fa";
import { useState, useCallback, useMemo, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearCampaignCreatedId,
  createCampaign,
  deleteCampaign,
  listCampaigns,
  setCampaignsPage,
  selectCampaign,
  updateCampaign,
  cloneCampaign,
  clearCampaignList,
  getCampaignListStats,
} from "./campaignSlice";
import { Campaign, CAMPAIGN_CONTEXT } from "../../../common/types/campaign";
import { PERSON_ORG_MAPPING } from "../../../common/types/person";
import { DeleteConfirmationModal } from "../../../components/DeleteConfirmationModal";
import {
  isInit,
  isLoading,
  isSuccess,
  keepClickEventRestricted,
  percentageOf,
} from "../../../common/helper/commonHelper";
import urls from "../../../urls";
import SpinnerContainer from "../../../components/SpinnerContainer";
import IButton, { BUTTON } from "../../../components/IButton";
import EditNameModal, {
  ASSET_NAME_ACTIONS,
} from "../../../components/EditNameModal";
import {
  selectSettings,
  resetLoadingStateEmailDefault,
} from "../settings/settingsSlice";
import GlobalDefaultsModal from "../../../components/GlobalDefaultsPopup";
import {
  CAMPAIGN_STATUS_COLORS,
  CAMPAIGN_TYPE,
} from "../../../common/constants/campaign";
import { debounce } from "lodash";
import { CommonListHeader } from "../../../components/CommonListHeader";
import { FormatDate } from "../../../components/DateTimeRangeFilter";
import TagSideBar from "../tag/TagSideBar";
import { createColumnHelper } from "@tanstack/react-table";
import { DataTable } from "../../../components/data-table/DataTable";
import { RiDraftFill } from "react-icons/ri";
import { selectTag } from "../tag/tagSlice";
import IconWithTooltip from "../../../components/IconWithTooltip";
import { TAG_CONNECTOR } from "../../../common/constants/common";
import WrapperWithSkeleton from "../../../components/WrapperWithSkeleton";

const initState: Campaign = {
  campaign_id: "",
  created_at: "",
  draft: false,
  name: "",
  schedule: null,
  created_by: {
    id: 0,
    name: "",
  },
  updated_at: "",
  active: false,
  count_processed: null,
  count_delivered: null,
  count_bounced: null,
  count_dropped: null,
  open_count: null,
  total_count: null,
};

function CampaignHeader({
  openEditableModal,
  loading,
  searchKeyword,
  onSearch,
}: {
  openEditableModal: () => void;
  loading?: boolean;
  searchKeyword: string;
  onSearch: (keyword: string) => void;
}) {
  return (
    <CommonListHeader
      heading="Journeys"
      createButtonProps={{
        onClick: openEditableModal,
        isLoading: loading,
        name: "new-campaign",
        text: "New Journey",
      }}
      searchInputProps={{
        placeholder: "Search journeys",
        value: searchKeyword,
        name: "search-input",
        onSearch: onSearch,
      }}
    />
  );
}

function EmptyPageCallToAction({
  openEditableModal,
  loading,
}: {
  openEditableModal: () => void;
  loading?: boolean;
}) {
  return (
    <Box
      minH="calc(100% - 88px)"
      display="flex"
      justifyContent="center"
      alignItems="center"
      flexDir="column"
      mt="20px"
    >
      <VStack spacing={6}>
        <Box>
          <CampaignCallout />
        </Box>
        <Text fontWeight={"bold"} fontSize="xl">
          Let’s add a journey to get started
        </Text>
        <Text fontSize="sm" fontWeight="semibold" textAlign="center">
          There doesn't seem to be anything here, why <br /> don't you create a
          new journey?
        </Text>
        <Box>
          <IButton
            isLoading={loading}
            variant={BUTTON.PRIMARY}
            size="lg"
            onClick={openEditableModal}
            name="create-new-campaign"
          >
            Create first journey
          </IButton>
        </Box>
      </VStack>
    </Box>
  );
}

function EmptyListComponent({
  asset,
  filteringBySearch,
  filteringByTags,
  children,
}: {
  asset: string;
  filteringBySearch?: boolean;
  filteringByTags?: boolean;
  children?: ReactNode;
}) {
  if (filteringBySearch || filteringByTags) {
    return (
      <Center w="100%" h="300px" fontSize="14px" color="text.100">
        <Text>
          No {`${asset}`} found. Please change the search / filter values.
        </Text>
      </Center>
    );
  }
  return (
    <Center w="100%" h="calc(100vh - 120px)">
      {children}
    </Center>
  );
}

function CellWithPercentage({
  data,
  percentage,
  color,
}: {
  data: number;
  percentage: string;
  color: string;
}) {
  return data ? (
    <Flex justifyContent="flex-end">
      <Text color={color} fontWeight="bold">
        {percentage}
      </Text>
    </Flex>
  ) : (
    <Box>-</Box>
  );
}

function CampaignStatusIcon({
  draft,
  active,
}: {
  draft: boolean;
  active: boolean;
}) {
  if (active)
    return (
      <IconWithTooltip
        icon={FaPlayCircle}
        color={CAMPAIGN_STATUS_COLORS.Active}
        label="Active"
      />
    );
  if (draft)
    return (
      <IconWithTooltip
        icon={RiDraftFill}
        color={CAMPAIGN_STATUS_COLORS.Draft}
        label="Draft"
      />
    );
  return (
    <IconWithTooltip
      icon={FaPauseCircle}
      color={CAMPAIGN_STATUS_COLORS.Inactive}
      label="Inactive"
    />
  );
}

function CampaignListTable({
  loading,
  campaignList,
  editCampaignHandler,
  openCloneModal,
  openRenameModal,
  openDeleteModal,
  totalPageCount,
  currentPage,
  emptyMsg,
}: {
  loading: boolean;
  campaignList: Campaign[] | null;
  editCampaignHandler: (row: Campaign) => void;
  openCloneModal: (row: Campaign) => void;
  openRenameModal: (row: Campaign) => void;
  openDeleteModal: (row: Campaign) => void;
  totalPageCount: number | null;
  currentPage: number;
  emptyMsg: string;
}) {
  const dispatch = useDispatch();
  const {
    tagSummaryList: { data: tagSummaryList },
  } = useSelector(selectTag);

  const columnHelper = createColumnHelper<Campaign>();

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        cell: (info) => {
          info.getValue();
          const typeSpecifics =
            CAMPAIGN_TYPE[
              info.row.original.campaign_context ?? CAMPAIGN_CONTEXT.PERSON
            ];
          return (
            <Box>
              <HStack alignItems="flex-start" flex="1">
                <CampaignStatusIcon
                  draft={info.row.original.draft}
                  active={info.row.original.active}
                />
                <IconWithTooltip
                  icon={typeSpecifics.icon}
                  color={typeSpecifics.color}
                  fontSize="14px"
                  label={typeSpecifics.label}
                />
                <VStack alignItems="flex-start">
                  <Text>{info.getValue()}</Text>
                  <Wrap>
                    {info.row.original.tags?.map((tag) => {
                      return tagSummaryList[tag] ? (
                        <Text fontSize="12px" color="gray.500">
                          #{tagSummaryList[tag]}
                        </Text>
                      ) : (
                        <></>
                      );
                    })}
                  </Wrap>
                </VStack>
              </HStack>
            </Box>
          );
        },
        header: "Journey",
        minSize: 700,
      }),
      columnHelper.accessor("total_count", {
        cell: (info) => {
          return (
            <WrapperWithSkeleton
              loading={isLoading(info.row.original.stats_loading)}
            >
              {info.getValue()?.toLocaleString() ?? "-"}
            </WrapperWithSkeleton>
          );
        },
        header: "Total",
        meta: {
          isNumeric: true,
          width: "80px",
        },
      }),
      columnHelper.accessor("count_delivered", {
        cell: (info) => {
          return (
            <WrapperWithSkeleton
              loading={isLoading(info.row.original.stats_loading)}
            >
              <CellWithPercentage
                data={info.getValue() ?? 0}
                percentage={
                  percentageOf(
                    info.getValue() ?? 0,
                    info.row.original.count_processed ?? 0,
                    true
                  ) as string
                }
                color="blue.600"
              />
            </WrapperWithSkeleton>
          );
        },
        header: "Delivered",
        meta: {
          isNumeric: true,
        },
        size: 150,
      }),
      columnHelper.accessor("open_count", {
        cell: (info) => {
          return (
            <WrapperWithSkeleton
              loading={isLoading(info.row.original.stats_loading)}
            >
              <CellWithPercentage
                data={info.getValue() ?? 0}
                percentage={
                  percentageOf(
                    info.getValue() ?? 0,
                    info.row.original.count_delivered ?? 0,
                    true
                  ) as string
                }
                color="cyan.600"
              />
            </WrapperWithSkeleton>
          );
        },
        header: "Opened",
        meta: {
          isNumeric: true,
        },
        size: 150,
      }),
      columnHelper.accessor("schedule", {
        cell: (info) => (
          <FormatDate date={info.getValue()?.next_run_time} splitLines />
        ),
        header: "Next schedule",
        minSize: 200,
      }),

      columnHelper.display({
        cell: (info) => {
          return (
            <Flex alignItems="center" justifyContent="flex-end">
              <Box onClick={(e) => keepClickEventRestricted(e)}>
                <Menu>
                  <MenuButton
                    size="sm"
                    as={IconButton}
                    aria-label="Options"
                    variant="ghost"
                    name="options"
                    icon={<FaEllipsisV />}
                  />
                  <MenuList rootProps={{ style: { right: 0 } }}>
                    <MenuItem
                      onClick={() => openCloneModal(info.row.original)}
                      name="clone-item"
                    >
                      Clone
                    </MenuItem>
                    <MenuItem
                      onClick={() => openRenameModal(info.row.original)}
                      name="rename-item"
                    >
                      Rename
                    </MenuItem>
                    <MenuItem
                      onClick={() => openDeleteModal(info.row.original)}
                      name="remove-item"
                    >
                      Remove
                    </MenuItem>
                  </MenuList>
                </Menu>
              </Box>
            </Flex>
          );
        },
        header: "",
        id: "actions",
        size: 50,
      }),
    ],
    [
      columnHelper,
      openCloneModal,
      openDeleteModal,
      openRenameModal,
      tagSummaryList,
    ]
  );

  return (
    <DataTable
      list={campaignList}
      loading={loading}
      onRowClick={editCampaignHandler}
      setPage={(pageNo: number) => dispatch(setCampaignsPage(pageNo))}
      totalPageCount={totalPageCount}
      currentPage={currentPage}
      columns={columns}
      emptyMsg={emptyMsg}
      links={{ to: urls.campaign, editParam: "campaign_id" }}
    />
  );
}

export default function Campaigns() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    campaignsChanged,
    campaignList,
    campaignCreatedId,
    creatingCampaign,
  } = useSelector(selectCampaign);

  const {
    isOpen: isOpenGlobalDefaultsModal,
    onOpen: onOpenGlobalDefaultsModal,
    onClose: onCloseGlobalDefaultsModal,
  } = useDisclosure();

  const { globalDefault, personOrgMapping } = useSelector(selectSettings);

  const [editableName, setEditableName] = useState<string | null>(null);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [isOpenCloneModal, setIsOpenCloneModal] = useState(false);
  const [campaignContext, setCampaignContext] = useState<CAMPAIGN_CONTEXT>(
    CAMPAIGN_CONTEXT.PERSON
  );
  const [selectedRow, setSelectedRow] = useState<Campaign>(initState);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const filterColumns = ["name"];

  const checkGlobalDefaults = useCallback(() => {
    if (
      isSuccess(globalDefault.fetching) &&
      (!globalDefault.data?.from_email.email ||
        !globalDefault.data?.from_email.name ||
        !globalDefault.data.reply_to)
    ) {
      onOpenGlobalDefaultsModal();
    } else {
      setEditableName("");
    }
  }, [globalDefault, onOpenGlobalDefaultsModal]);

  useEffect(() => {
    return () => {
      dispatch(clearCampaignList());
    };
  }, [dispatch]);

  const editCampaign = useCallback(
    (id: string) => {
      navigate(`${urls.journey}/${id}`);
    },
    [navigate]
  );

  useEffect(() => {
    dispatch(
      listCampaigns({
        searchKeyword,
        filterColumns,
        tagsFilter: filterTags.length
          ? { tag_ids: filterTags, connector: TAG_CONNECTOR.OR }
          : undefined,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, campaignsChanged, campaignList.currentPageNo]);

  useEffect(() => {
    if (
      campaignList.list?.length &&
      campaignList.list.every((campaign) => isInit(campaign.stats_loading))
    ) {
      dispatch(
        getCampaignListStats(
          campaignList.list.map((campaign) => campaign.campaign_id)
        )
      );
    }
  }, [campaignList.list, dispatch]);

  useEffect(() => {
    if (campaignCreatedId !== "") {
      editCampaign(campaignCreatedId);
      dispatch(clearCampaignCreatedId());
    }
  }, [campaignCreatedId, editCampaign, dispatch]);

  function addCampaignHandler(newName: string) {
    setEditableName(null);
    dispatch(
      createCampaign({ name: newName, campaignContext: campaignContext })
    );
  }

  function cloneCampaignHandler(name: string) {
    if (selectedRow && selectedRow.campaign_id) {
      dispatch(cloneCampaign({ campaignId: selectedRow.campaign_id, name }));
    }
    setIsOpenCloneModal(false);
  }

  function deleteCampaignHandler() {
    setIsOpenDeleteModal(false);
    if (selectedRow && selectedRow.campaign_id) {
      dispatch(deleteCampaign(selectedRow.campaign_id));
    }
  }

  function renameCampaignHandler(updatedName: string) {
    setEditableName(null);
    if (selectedRow && selectedRow.campaign_id) {
      dispatch(
        updateCampaign({ id: selectedRow.campaign_id, name: updatedName })
      );
    }
  }

  function openCloneModal(row: Campaign) {
    setSelectedRow(row);
    setIsOpenCloneModal(true);
  }

  function openDeleteModal(row: Campaign) {
    setIsOpenDeleteModal(true);
    setSelectedRow(row);
  }

  function openRenameModal(row: Campaign) {
    setEditableName(row.name);
    setSelectedRow(row);
  }

  function editCampaignHandler(row: Campaign) {
    setSelectedRow(row);
    editCampaign(row.campaign_id);
  }

  useEffect(() => {
    return () => {
      dispatch(resetLoadingStateEmailDefault());
    };
  }, [dispatch]);

  // Support for old urls | Replacing old urls to new
  useEffect(() => {
    navigate(urls.journey, { replace: true });
  }, [navigate]);

  const searchIfValid = useCallback(
    (keyword: string, tags: string[]) => {
      if (campaignList.currentPageNo !== 1) {
        dispatch(setCampaignsPage(1));
      } else {
        dispatch(
          listCampaigns({
            searchKeyword: keyword,
            filterColumns,
            tagsFilter: tags.length
              ? { tag_ids: tags, connector: TAG_CONNECTOR.OR }
              : undefined,
          })
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, campaignList.currentPageNo]
  );

  const debouncedSearch = useMemo(
    () => debounce(searchIfValid, 1000),
    [searchIfValid]
  );

  const isFiltersAdded = useMemo(
    () => !!searchKeyword || !!filterTags.length,
    [filterTags, searchKeyword]
  );

  function setAndSearchWithTag(tags: string[]) {
    setFilterTags(tags);
    searchIfValid(searchKeyword, tags);
  }

  return (
    <>
      <CampaignHeader
        loading={isLoading(creatingCampaign)}
        openEditableModal={checkGlobalDefaults}
        searchKeyword={searchKeyword}
        onSearch={(keyword: string) => {
          setSearchKeyword(keyword);
          debouncedSearch(keyword, filterTags);
        }}
      />
      <Flex w="100%">
        {(isFiltersAdded || !!campaignList.totalPageCount) && (
          <Box h="80vh" minW="280px" maxW="280px" pl="20px">
            <TagSideBar onSelection={setAndSearchWithTag} />
          </Box>
        )}
        <SpinnerContainer
          height="350px"
          loading={
            (campaignList.fetchingList && !campaignList.changingPage) ||
            campaignList.totalPageCount === null ||
            isLoading(globalDefault.fetching)
          }
        >
          <Box w="100%" h="100%" pl="15px" pr="20px">
            {campaignList.totalPageCount ? (
              <CampaignListTable
                loading={campaignList.fetchingList}
                campaignList={campaignList.list}
                editCampaignHandler={editCampaignHandler}
                openCloneModal={openCloneModal}
                openRenameModal={openRenameModal}
                openDeleteModal={openDeleteModal}
                totalPageCount={campaignList.totalPageCount}
                currentPage={campaignList.currentPageNo}
                emptyMsg={`No journeys found.${
                  searchKeyword
                    ? " Please change the search / filter values"
                    : ""
                }`}
              />
            ) : (
              <EmptyListComponent
                asset="journeys"
                filteringBySearch={!!searchKeyword}
                filteringByTags={!!filterTags.length}
              >
                <EmptyPageCallToAction
                  loading={isLoading(creatingCampaign)}
                  openEditableModal={checkGlobalDefaults}
                />
              </EmptyListComponent>
            )}
          </Box>
        </SpinnerContainer>
      </Flex>
      {/* create and rename the campaign */}
      <EditNameModal
        value={editableName ?? ""}
        action={
          editableName ? ASSET_NAME_ACTIONS.RENAME : ASSET_NAME_ACTIONS.CREATE
        }
        isOpen={editableName !== null}
        onClose={() => setEditableName(null)}
        onSubmit={
          editableName === "" ? addCampaignHandler : renameCampaignHandler
        }
        asset="journey"
      >
        {editableName === "" &&
          personOrgMapping !== PERSON_ORG_MAPPING.ONE_TO_NONE && (
            <RadioGroup
              onChange={(value: string) =>
                setCampaignContext(value as CAMPAIGN_CONTEXT)
              }
              value={campaignContext}
              mt={2}
            >
              <Radio
                value={CAMPAIGN_CONTEXT.PERSON}
                style={{ cursor: "pointer" }}
              >
                <Box p={2} mb={2} style={{ cursor: "pointer" }}>
                  <Text fontSize="16px" fontWeight="600">
                    Send Email to a Marketing Contact/Lead
                  </Text>
                  <Text>This option would send only one email per contact</Text>
                </Box>
              </Radio>
              <Radio value={CAMPAIGN_CONTEXT.ORG} style={{ cursor: "pointer" }}>
                <Box p={2} style={{ cursor: "pointer" }}>
                  <Text fontSize="16px" fontWeight="600">
                    Send Email to Product Users
                  </Text>
                  <Text>
                    {" "}
                    This option would send email to each user (Email+OrgID) in
                    the system
                  </Text>
                </Box>
              </Radio>
            </RadioGroup>
          )}
      </EditNameModal>

      <DeleteConfirmationModal
        isOpen={isOpenDeleteModal}
        onCancel={() => setIsOpenDeleteModal(false)}
        submitHandler={deleteCampaignHandler}
      >
        <Text>
          Are you sure about deleting this journey? Once deleted cannot be
          undone
        </Text>
      </DeleteConfirmationModal>

      <GlobalDefaultsModal
        isOpen={isOpenGlobalDefaultsModal}
        onClose={onCloseGlobalDefaultsModal}
      />
      {/* cloning the campaign */}
      <EditNameModal
        value={selectedRow.name + "-clone"}
        action={ASSET_NAME_ACTIONS.CLONE}
        isOpen={isOpenCloneModal}
        onClose={() => setIsOpenCloneModal(false)}
        onSubmit={cloneCampaignHandler}
        asset="Journey"
      >
        <HStack
          alignItems="flex-start"
          fontSize="sm"
          color="gray.700"
          mt={2}
          p={2}
          background="grayV2.100"
        >
          <Icon
            color="brand.black"
            fontSize="14px"
            as={FaExclamationCircle}
            mt={1}
          ></Icon>
          <Text>
            The dynamic list, flow steps and exit criteria would be cloned from
            <Text
              as="span"
              color="brand.black"
              fontWeight="bold"
              overflowWrap={"anywhere"}
            >
              {` "${selectedRow.name}" `}
            </Text>
            journey.
          </Text>
        </HStack>
      </EditNameModal>
    </>
  );
}
